import _ from 'lodash'
import Vue from 'vue'
import moment from 'moment'
import Randomizer from './randomizer'
import {
    CONFIG_TO_FIELDS,
    CONST,
    DEFAULT_DATE,
    DEFAULT_FLAGS,
    FIELD_PROPERTIES_KEYS,
    FORM_PROPERTIES,
    RULES,
    REGEX_EMAIL
} from './constants'
import ColorConvert from 'color-convert'
import Ranger from './ranger'
import CONSTANTS from '@src/constants'
import { RatingField } from './rating'

const dateRangeMap = new Map()

// Gridform Rows Definition Helper
// LocalStorage Wrapper
const Helper = {
    i18n: () => 'placeholder_function', // placeholder for translations with binded this.$vuetify.lang.t

    init(gf, name) {
        const gfdef = _.cloneDeep(gf)
        if (!this.validateRows(gfdef.rows)) {
            console.error('invalid gridform rows configuration detected', gfdef.rows)
        }

        _.defaults(gfdef, FORM_PROPERTIES)

        if (gfdef.flat) gfdef.elevation = 0

        // get saved data from local storage
        const storeKey = this.storeKey(name)
        const defValues = {}
        if (gfdef.store) {
            _.each(this.getStore(storeKey), (fls) => {
                defValues[fls.name] = {value: fls.value}
            })
        }
        else {
            this.removeStore(storeKey)
        }

        // pick all options we want to copy from the form config to field config
        const fieldOptions = _.pick(gfdef, CONFIG_TO_FIELDS)

        // init field definitions
        gfdef.rows.forEach(row => {
            row.forEach(col => {
                _.extend(col, fieldOptions)
                if (
                    col.name &&
                    defValues[col.name] &&
                    !_.isUndefined(defValues[col.name].value)
                ) {
                    col.value = defValues[col.name].value
                }
            })
        })

        return gfdef
    },
    initRows(rows, fieldlist) {
        rows.forEach(row => {
            // calculate defined row sizes
            let colCount = row.length
            let colSum = 12
            row.forEach(col => {
                if (col.size) {
                    colCount--
                    colSum -= col.size
                }
            })
            // calculate missing col size
            // TODO prevent float values (3 cols, row 1: size=7, row 2+3: size=2.5)
            row.forEach(col => {
                if (_.isUndefined(col.size) && colCount) col.size = colSum / colCount
                if (fieldlist) col.fieldlist = fieldlist
            })
        })
    },
    initField(col, defValues = {}, fieldOptions = {}) {
        // set field defaults
        _.defaults(col, DEFAULT_FLAGS, fieldOptions)

        // init values
        col.type = col.type || 'text'
        switch(col.type) {
            case 'textarea':
                col.value = col.value || ''
                col.rows = col.rows || CONST.TEXTAREA_ROWS
                break
            case 'checkbox':
            case 'switch':
                col.value = this.bool(col.value, false)
                break
            case 'rating':
                col.value = col.value || 0
                break
            case 'number':
                col.value = _.isNumber(col.value) ? col.value : col.min
                break
            case 'slider':
                col.value = col.value || col.min
                break
            case 'range':
                col.value = _.isArray(col.value) ? col.value : []
                break
            case 'spacer':
                col.readonly = true
                break
        }

        // TODO disable type switch temporary and convert to checkbox
        if (col.type === 'switch') col.type = 'checkbox'

        col.rollback = col.value
        // check def only for columns with name
        if (
            col.name &&
            defValues[col.name] &&
            !_.isUndefined(defValues[col.name].value)
        ) {
            col.value = defValues[col.name].value
        }

        // ensure unique column name
        col.name = col.name || _.uniqueId('vgf-f')
        // clone rules from definition
        if (Array.isArray(col.rules)) {
            col.frules = [...col.rules]
        }
        col.rules = this.rules(col)
    },

    // build field rules list
    rules(field) {
        const partialRules = rules => rules.map(rule => _.partialRight(rule, field))

        let rules = []

        // rules are sorted by priority
        // NOTE we check first for valid email, the required logic is included
        //  and i want to display "Enter valid email" before "Required"
        if (this.isType(field, 'email')) rules = rules.concat(RULES.emailRules)

        if (field.required) {
            if (this.isType(field, 'checkbox')) rules = rules.concat(RULES.checkboxRequired)
            else rules = rules.concat(RULES.termRules)
        }

        if (this.isType(field, 'date')) rules = rules.concat(RULES.dateRules)
        else if (this.isType(field, 'time')) rules = rules.concat(RULES.timeRules)
        else if (this.isType(field, 'number') && !_.isUndefined(field.min) && !_.isUndefined(field.max)) {
            rules = rules.concat([
                v => (v >= field.min && v <= field.max) || 'Wert ist nicht im gültigen Bereich!'
            ])
        }

        if (field.minlength || field.minLength) rules = rules.concat(partialRules(RULES.minLengthRules))

        if (Array.isArray(field.rules)) rules = rules.concat(field.rules)

        return _.uniq(rules)
    },

    initFieldlist(col, rows) {
        // no fieldlist, set dummy
        if (!col.fieldlist) col.fieldlist = _.clone(CONSTANTS.DEFAULT_FIELDLIST)
        // no initials, guess it
        if (!col.fieldlist.initials) {
            const header = _.flatten(rows).find(col => col.type === 'subheader')
            if (header && header.label) col.fieldlist.initials = header.label.toLocaleUpperCase()
            col.fieldlist.initials = col.fieldlist.initials || col.fieldlist.id
        }
    },
    getFieldlist(obj) {
        let col = obj
        if (_.isArray(obj)) col = _.isArray(obj[0]) ? obj[0][0] : obj[0]
        return col ? {...col.fieldlist} : null
    },
    // convert fieldlist object {fieldlist: {}, index, rows: [], errors: [], errorCount}
    // to rows object, option to update fieldlist in fields
    fieldlistToRows(fl, updateFieldlist) {
        let rows = []
        _.each(fl, fl => {
            if (updateFieldlist) {
                _.each(_.flatten(fl.rows), field => {
                    field.fieldlist = fl.fieldlist
                })
            }
            rows = _.concat(rows, fl.rows)
        })
        return rows
    },

    setToggleState(_rows) {
        const rows = _.flatten(_rows)
        const toggles = rows.filter(field => !_.isEmpty(field.toggle)).map(field => ({toggle: field.toggle, collapsed: field.collapsed}))

        let hide = []
        let show = []
        toggles.forEach(t => {
            if (t.collapsed) hide = _.concat(hide, t.toggle)
            else show = _.concat(show, t.toggle)
        })

        if (hide.length || show.length) {
            rows.forEach(field => {
                if (hide.includes(field.name) && !field.hidden) field.hidden = true
                if (show.includes(field.name) && field.hidden) field.hidden = false
            })
        }
    },

    // build rating object depending on field type
    // calculate missing field props (slider, range = ticklabels from min, max or min, max from ticklabels)
    // set maxValue for each type to update field value to max score
    //   slider = value of last tick (not last tick label which is max score, but not the value to set)
    //   checkbox = true
    //   range = min to max
    rating(field) {
        const ratingField = new RatingField(field)
        return ratingField.calced
    },
    ratings(rows, weighting) {
        let sumScore = 0
        let sumWScore = 0
        let sumMax = 0
        let sumWMax = 0
        _.each(_.flatten(rows), col => {
            // recalc current rating for this field
            if (col.rating !== false) col.rating = this.rating(col)
            // exclude field with no rating
            if (col.rating && !col.rating.noRating && col.rating.max) {
                // do not add rating when fieldlist is excluded
                const flNoRating = col.fieldlist ? !!col.fieldlist.noRating : false
                if (!flNoRating) {
                    sumScore += col.rating.score
                    sumWScore += col.rating.wscore
                    sumMax += col.rating.max
                    sumWMax += col.rating.wmax
                }
            }
        })

        let rating = null
        if (sumMax) {
            let wscore = sumWScore
            let wmax = sumWMax
            if (weighting >= 0 && weighting < 100) {
                wscore = sumWScore * weighting / 100
                wmax = sumWMax * weighting / 100
            }
            // TODO calc with rounded sum?
            // wscore = Math.round(wscore)
            rating = this.buildRatingSum({score: sumScore, wscore, max: sumMax, wmax})
        }

        return rating
    },
    ratingsGrouped(_rows) {
        // make a copy of the objects in _rows, could be a vuex state
        const rows = _.cloneDeep(_rows)
        // only columns with fieldlist
        // collect all fields with rating
        // NOTE collecting by "fieldlist.id" ignores all fields without fieldlist
        const cols = _.flatten(rows).filter(col => !!col.rating)
        // give all fields a temporary fieldlist or fieldlist.initials
        _.each(cols, col => {
            this.initFieldlist(col, rows)
        })
        const grouped = _.groupBy(cols, 'fieldlist.initials')
        // group by short description, could be used as axis labels
        // NOTE all fields without fieldlist will be grouped by "_NOGROUP" (CONSTANTS.DEFAULT_FIELDLIST.id)
        const groups = {}
        _.each(grouped, (rows, id) => {
            // all rows in this group has the same fieldlist
            // so grab weighting from the first row
            const weighting = _.get(rows, '0.fieldlist.weighting')
            const rating = this.ratings(rows, weighting) // calculate sum for this fieldlist
            if (rating) groups[id] = rating // fieldlist with noRating returns null
        })
        return groups
    },
    ratingsGroupedSum(_rows) {
        let score = 0
        let wscore = 0
        let max = 0
        let wmax = 0
        const grouped = this.ratingsGrouped(_rows)
        _.each(grouped, g => {
            score += g.score
            wscore += _.isUndefined(g.wscore) ? g.score : g.wscore
            max += g.max
            wmax += g.wmax
        })
        return this.buildRatingSum({score, wscore, max, wmax})
    },
    buildRatingSum({score, wscore, max, wmax} = {}) {
        const percent = max ? score * 100 / max : 0
        const label = `${Math.round(wscore)}/${Math.round(wmax)}`
        const diff = score - wscore
        const wlabel = score !== wscore ? `${score}/${max}` : ''
        const color = this.percentToColor(percent, 0.5)
        const progressColor = this.percentToColor(percent)
        const hint = `${this.percentToGrade(percent)} (${Math.round(percent)}%)`
        const labelPercent = !_.isNaN(percent) ? `${Math.round(percent)}%` : '0'
        return {score, wscore, max, wmax, percent, label, wlabel, diff, color, progressColor, hint, labelPercent}
    },

    // create update payload to maximize a rating for a field
    ratingMax(item) {
        const update = {}

        const addUpdate = (field) => {
            const rating = field.rating
            if (!rating) return

            // we must check both props, using building block fab has type and block fab has _type
            const fieldType = field.type || field._type
            const maxValue = rating.maxValue

            // set to max or if already max, set to min
            // some field types needs to set his items property (multiple = true)
            // other the value of the selected item (multiple = false)
            if (maxValue === 'items') {
                if (field.multiple) {
                    // TODO when to set [value, value, ..] and when [{item}, {item}, ..]?
                    // update[field.name] = {value: field.items.map(item => item.value)}
                    // NEEDED FOR fieldType === 'combobox' (fab:max)
                    update[field.name] = {value: field.items}
                }
                else {
                    let max = field.max
                    // NOTE hack for missing value in building block "note"
                    if (fieldType === 'radio' && /^note/.test(field.value) && !field.max) max = 'note1'
                    // if no max is defined, search in items
                    if (_.isUndefined(max)) {
                        const maxItem = _.maxBy(field.items, item => Number(item.avatar))
                        // NOTE do we need a string value or the item object?
                        max = fieldType === 'combobox' ? maxItem : maxItem.value
                    }
                    update[field.name] = {value: max}
                }
            }
            // identify field types that have no scoring over value
            // like text or textarea
            else if (maxValue !== 'rating') {
                if (fieldType === 'range') {
                    const value = field.value
                    value[0] = field.min
                    value[1] = maxValue
                    update[field.name] = {value}
                }
                else {
                    update[field.name] = {value: maxValue}
                }
            }
            else {
                update[field.name] = {
                    rating: {
                        ...rating,
                        score: rating.max
                    }
                }
            }
        }

        // it is a fieldlist (and not a textarea with rows)
        if (_.has(item, 'rows') && Array.isArray(item.rows)) {
            _.flatten(item.rows).forEach(field => {
                addUpdate(field)
            })
        }
        // it is a field
        else addUpdate(item)

        return update
    },
    // create update payload to maximize a rating for a field
    ratingMin(item) {
        const update = {}

        const addUpdate = (field) => {
            const rating = field.rating
            if (!rating) return

            // we must check both props, using building block fab has type and block fab has _type
            const fieldType = field.type || field._type
            const minValue = rating.min
            const maxValue = rating.maxValue

            // set to min
            // some field types needs to set his items property (multiple = true)
            // other the value of the selected item (multiple = false)
            if (maxValue === 'items') {
                if (field.multiple) update[field.name] = {value: []}
                else {
                    let min = field.min
                    // NOTE hack for missing value in building block "note"
                    if (fieldType === 'radio' && /^note/.test(field.value) && !field.min) {
                        min = 'note6'
                    }
                    // if no min is defined, search in items
                    if (_.isUndefined(min)) {
                        const minItem = _.minBy(field.items, item => Number(item.avatar))
                        min = fieldType === 'combobox' ? minItem : minItem.value
                    }
                    update[field.name] = {value: min}
                    // the initial value of a combobox or radio could be = ""
                    // which means "no item" and score == 0
                    if (fieldType === 'radio') {
                        update[field.name] = {
                            value: min,
                            rating: {
                                ...rating,
                                score: 0
                            }
                        }
                    }
                }
            }
            // identify field types that have no scoring over value
            // like text or textarea
            else if (maxValue !== 'rating') {
                if (fieldType === 'range') {
                    const value = field.value
                    value[0] = minValue
                    value[1] = minValue
                    update[field.name] = {value}
                }
                else {
                    update[field.name] = {value: minValue}
                }
            }
            else update[field.name] = {
                    rating: {
                        ...rating,
                        score: minValue
                    }
                }
        }

        // it is a fieldlist (and not a textarea with rows)
        if (_.has(item, 'rows') && Array.isArray(item.rows)) _.flatten(item.rows).forEach(field => {addUpdate(field)})
        // it is a field
        else addUpdate(item)

        return update
    },

    // convert a duration string (hh:mm:ss) to seconds
    durationToSec(duration) {
        if (!duration) return duration

        const parts = `${duration}`.split(':')
        const hh = parseInt(parts[0], 10) * 60 * 60
        const mm = parseInt(parts[1], 10) * 60
        const ss = parseInt(parts[2], 10)
        return hh + mm + ss
    },

    // @link https://www2.hu-berlin.de/chemie/ernsting/lectures/Notenschluessel.html
    percentToGrade(percent) {
        let grade = ''
        switch (true) {
            case percent >= 95:
                grade = 'sehr gut (1)'
                break
            case percent >= 90:
                grade = 'sehr gut (1-)'
                break
            case percent >= 85:
                grade = 'gut (2+)'
                break
            case percent >= 80:
                grade = 'gut (2)'
                break
            case percent >= 75:
                grade = 'gut (2-)'
                break
            case percent >= 70:
                grade = 'befriedigend (3+)'
                break
            case percent >= 65:
                grade = 'befriedigend (3)'
                break
            case percent >= 60:
                grade = 'befriedigend (3-)'
                break
            case percent >= 55:
                grade = 'ausreichend (4+)'
                break
            case percent >= 50:
                grade = 'ausreichend (4)'
                break
            case percent < 50:
                grade = 'nicht ausreichend (5)'
                break
        }
        return grade
    },
    percentToColorNew(percent, alpha = 1) {
        let color = ''
        switch (true) {
            case percent >= 95:
                color = '#029924'
                break
            case percent >= 90:
                color = '#029924'
                break
            case percent >= 85:
                color = '#49BA4E'
                break
            case percent >= 80:
                color = '#9CCC65'
                break
            case percent >= 75:
                color = '#7fed7d'
                break
            case percent >= 70:
                color = '#ffee4d'
                break
            case percent >= 65:
                color = '#F5BC00'
                break
            case percent >= 60:
                color = '#bd8c00'
                break
            case percent >= 55:
                color = '#ff563d'
                break
            case percent >= 50:
                color = '#d91111'
                break
            case percent < 50:
                color = '#9f0000'
                break
        }
        if (alpha === 1) return color
        const rgb = ColorConvert.hex.rgb(color)
        return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`
    },
    percentToColor(percent, alpha = 1) {
        let color = ''
        switch (true) {
            case percent >= 95:
                color = '#518F0A'
                break
            case percent >= 90:
                color = '#7CB342'
                break
            case percent >= 85:
                color = '#A0C53F'
                break
            case percent >= 80:
                color = '#9CCC65'
                break
            case percent >= 75:
                color = '#C8E37F'
                break
            case percent >= 70:
                color = '#FFEE58'
                break
            case percent >= 65:
                color = '#FFEB3B'
                break
            case percent >= 60:
                color = '#EECA2C'
                break
            case percent >= 55:
                color = '#F3A729'
                break
            case percent >= 50:
                color = '#F17D0E'
                break
            case percent < 50:
                color = '#D24444'
                break
        }
        if (alpha === 1) return color
        const rgb = ColorConvert.hex.rgb(color)
        return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`
    },

    // "GFCHANGE", "gfName"
    // "GFCHANGE"
    // "", "gfName"
    ns(action, name) {
        const _action = action ? `/${action}` : ''
        if (!name) return `${CONST.NAMESPACE}${_action}`
        return `${CONST.NAMESPACE}/_${name}${_action}`
    },
    storeKey(name) {
        return `${CONST.NAMESPACE}-${name}-lstore`
    },

    rebuildField(field, payload) {
        if (!payload) return field
        const props = payload[field.name]
        if (!props) return field

        const validProps = _.pick(props, FIELD_PROPERTIES_KEYS)
        if (_.isEmpty(validProps)) {
            console.warn('rebuildField "%s": no valid props found!', field.name, props)
            return field
        }

        // check value changes
        // TODO put to UTILS or in fields
        if (_.has(validProps, 'value')) {
            field.dirty = true
            if (field.type === 'date') {
                const dte = validProps.value ? moment(validProps.value, DEFAULT_DATE.format, true) : ''
                // TODO reset value if invalid?
                if (dte && dte.isValid()) validProps.value = dte.format(DEFAULT_DATE.format)
            }
            else if (field.type === 'radio') {
                // TODO check multiple prop, but do not convert array to string
                // console.log('field.value, field.multiple', field.value, field.multiple, validProps)
                // validProps.value = (validProps.value || '').toString()
            }
            else if (field.type === 'number') {
                // do not convert blank to 0, blank means empty input and show placeholder
                if (validProps.value) validProps.value = parseInt(validProps.value, 10)
            }
            else if (field.type === 'text') {
                validProps.value = validProps.value || ''
                if (field.uppercase) validProps.value = validProps.value.toUpperCase()
            }
        }

        // check for changes otherwise skip
        const validKeys = _.keys(validProps)
        const current = _.pick(field, validKeys)
        if (_.isEqual(current, validProps)) return field
        // NOTE validProps = { value: undefined } is not empty or equal with { value: '' }
        if (_.isEmpty(current) && validKeys.length === 1 && validKeys[0] === 'value' && typeof validProps.value === 'undefined') return field

        const newField = Object.assign({}, field, validProps)
        newField.rules = Array.isArray(field.frules) ? [...field.frules] : [] // init rules from definition config
        newField.rules = this.rules(newField) // get rules for new field props
        newField.rating = this.rating(newField) // get rating for new field props
        return newField
    },

    isType(field, type) {
        return (field.type || 'text').toLowerCase() === type.toLowerCase()
    },
    isInputField(field) {
        if (!field || !field.name) return false
        return !field.readonly
    },
    isSuccessfulField(field) {
        let valid = true
        if (!field.value) valid = false // unsuccessful fields text="", number=0, checkbox=false
        if (field.readonly) valid = false
        return valid
    },

    // build a payload object for a list of fields with given props
    // return null for empty payload for easier if conditions
    // "if (payload)" instead of "if !_.isEmpty(payload)"
    buildPayload(fields, props) {
        const payload = {}
        _.each(fields, (field) => {
            payload[field.name] = props
        })
        return !_.isEmpty(payload) ? payload : null
    },
    // build a payload object from a form data object
    // [{name, value}] => {[name]: value}
    storeToPayload(formdata) {
        const payload = {}
        _.each(formdata, (field) => {
            payload[field.name] = { value: field.value }
        })
        return !_.isEmpty(payload) ? payload : null
    },
    // build a store object from a payload data object
    // {[name]: value} => [{name, value}]
    // NOTE value===null is handled as prop value doesn't exists
    // NOTE value===undefined is handled as prop value doesn't exists
    payloadToStore(payload) {
        const store = {}
        _.each(payload, (props, name) => {
            if (_.has(props, 'value') && !_.isNull(props.value) && !_.isUndefined(props.value)) store[name] = props.value
        })
        return !_.isEmpty(store) ? store : null
    },
    formToStore(payload) {
        const store = {}
        _.each(payload, (field) => {
            store[field.name] = field.value
        })
        return !_.isEmpty(store) ? store : null
    },
    objectToPayload(data) {
        const payload = {}
        _.each(data, (value, name) => {payload[name] = {value}})
        return !_.isEmpty(payload) ? payload : null
    },
    // convert array of fieldnames to update payload
    // ['name', 'label'], {disabled: true}
    arrayToPayload(fields, value) {
        const payload = {}
        _.each(fields, name => {payload[name] = value})
        return !_.isEmpty(payload) ? payload : null
    },

    // pick all keys from payload with a value prop
    reducerValue(payload) {
        const reduced = {}
        _.each(payload, (props, name) => {
            if (_.has(props, 'value')) reduced[name] = props.value
        })
        return reduced
    },
    // pick all keys from payload with a valid value prop
    reducerValid(payload) {
        const reduced = {}
        _.each(payload, (props, name) => {
            if (_.has(props, 'value') && !_.isUndefined(props.value)) reduced[name] = props.value
        })
        return reduced
    },

    hasDiffs(state, payload) {
        const payloadKeys = _.keys(payload)
        // console.log('hasDiffs', state, payload)
        // console.log('payloadKeys', payloadKeys)
        return !!_.find(payloadKeys, (plk) => {
            const field = _.find(state, (s) => s.name === plk)
            if (!field) return false

            const props = _.keys(payload[plk])
            // console.log(props)
            return _.find(props, (p) => {
                // console.log('p', plk, payload[plk][p] !== field[p], payload[plk][p], field[p])
                return payload[plk][p] !== field[p]
            })
        })
    },

    parseJSON(str) {
        try {
            return JSON.parse(str)
        } catch (e) {}
        // DEV ONLY FOR TESTS
        // try {
        //   return eval(str)
        // } catch (e) {}
    },
    parseRows(rows) {
        const _rows = this.parseJSON(rows)
        if (!_rows) return

        let _parsed
        if (_.has(_rows, 'config')) {
            _parsed = _rows.config
        } else if (!_.isArray(_rows) && _.isObject(_rows)) {
            _parsed = [[_rows]]
        } else if (_.isArray(_rows) && _.isArray(_rows[0])) {
            _parsed = _rows
        } else if (_.isArray(_rows) && _.isObject(_rows[0])) {
            _parsed = [_rows]
        }
        return _parsed
    },
    validateRows(rows) {
        const _rows = this.parseRows(rows)
        if (_.isEmpty(_rows)) return true // no rows are valid

        return _.isUndefined(
            _.find(_rows, (row) => {
                return _.find(row, (field) => !this.validateField(field))
            })
        )
    },
    validateField(field) {
        const isValid = _.has(field, 'name') || _.has(field, 'label') || _.has(field, 'type')
        if (!isValid) console.log('invalid field', JSON.stringify(field))
        return isValid
    },

    // validate a field and return error messages
    validate(field, data) {
        if (!field || _.isEmpty(field.rules) || field.exclude) return []
        // NOTE we could not set dirty=true when on input blur
        // if (!field.dirty) return [] // no changes until now

        const errors = []
        field.rules.forEach(rule => {
            const message = rule(field.value, data)
            if (message !== true && message) errors.push(message)
        })
        return errors
    },
    // validate over list of fields
    // find first rule that don't match
    validation(fields, data) {
        if (_.isEmpty(fields)) return true

        const invalidField = fields.find(field => {
            if (field.hidden) return false // skip fields we must not validate
            // call the rules not only with value
            // add current data to validate value depending on other fields values
            return field.rules.find(rule => rule(field.value, data) !== true)
        })
        return {valid: !invalidField, invalid: _.pick(invalidField, ['name', 'value'])}
    },

    // validate an email adress
    isValidEmail(email) {
        return REGEX_EMAIL.test(email)
    },

    // convert string code to object props for queries
    codeToQuery(code, date) {
        // NOTE currently only date codes available, other code later are possible
        return {...this.calcDate(code, date)}
    },
    // convert range shortcuts to date range
    // -1d = 1 day before today
    // -3M = 3 months before today
    // last-y = last year (from 01-01 to 12-31)
    calcDate(code, date = 'created') {
        if (!code) return

        code = code.replace(/^now/, '')
        const isLast = /^last/.test(code)
        code = code.replace(/^last/, '')
        const isSub = /^-/.test(code)
        code = code.replace(/^-/, '')

        const unit = code.slice(-1)
        const value = parseInt(code.substr(0, code.length - 1), 10) || 0
        const mom = moment()

        if (isLast) mom.subtract(1, unit)
        else if (isSub) mom.subtract(value, unit)

        if (unit) mom.startOf(unit) // now-y

        const range = {
            [date === 'finished' ? 'datefromf' : 'datefrom']: mom.format('YYYY-MM-DD'),
            [date === 'finished' ? 'timefromf' : 'timefrom']: mom.format('HH:mm')
        }
        if (isLast) {
            range[date === 'finished' ? 'datetof' : 'dateto'] = mom.endOf(unit).format('YYYY-MM-DD')
            range[date === 'finished' ? 'timetof' : 'timeto'] = mom.endOf(unit).format('HH:mm')
        }
        return range
    },
    // build datetime range from start date and time and end date and time
    // calculation results are cached
    // convert to local time
    calcDateRange(params) {
        if (_.isEmpty(params)) return
        let {dateFrom, timeFrom, dateTo, timeTo} = params
        // fallbacks to other name cases or finished fields
        if (!dateFrom && !timeFrom && !dateTo && !timeTo) {
            const {datefrom, timefrom, dateto, timeto} = params
            dateFrom = datefrom
            timeFrom = timefrom
            dateTo = dateto
            timeTo = timeto
        }
        if (!dateFrom && !timeFrom && !dateTo && !timeTo) {
            const {datefromf, timefromf, datetof, timetof} = params
            dateFrom = datefromf
            timeFrom = timefromf
            dateTo = datetof
            timeTo = timetof
        }
        if (!dateFrom && !timeFrom && !dateTo && !timeTo) return

        // check if date range is already calculated
        const fromMap = dateRangeMap.get(JSON.stringify(params))
        if (fromMap) return fromMap

        // only times -> set current date as default
        if (!dateFrom && timeFrom) dateFrom = moment().format('YYYY-MM-DD')
        if (!dateTo && timeTo) dateTo = moment().format('YYYY-MM-DD')

        // NOTE strict needs this format YYYY-MM-DD
        const dfrom = Ranger.parseDate(dateFrom, true)
        const tfrom = Ranger.parseTime(timeFrom)
        const dto = Ranger.parseDate(dateTo, true)
        const tto = Ranger.parseTime(timeTo)
        if (!dfrom && !dto) return

        const mfrom = dfrom ? moment(dfrom) : null
        if (mfrom && tfrom) mfrom.set(tfrom)

        const mto = dto ? moment(dto) : null
        if (mto) tto ? mto.set(tto) : mto.set({hour: 23, minute: 59})
        if (mto) mto.set({second: 59, millisecond: 999}) // always set seconds and millisecond to end for to

        const from = mfrom ? moment.utc(mfrom).local().toISOString() : null
        const to = mto ? moment.utc(mto).local().toISOString() : null

        const dateRange = {from, to}
        // put result to map
        dateRangeMap.set(JSON.stringify(params), dateRange)

        return dateRange
    },
    // no range is always true
    inDateRange(date, params) {
        const dateRange = this.calcDateRange(params)
        if (!dateRange) return true
        if (dateRange.from && moment(dateRange.from).isAfter(moment.utc(date))) return false
        return !(dateRange.to && moment(dateRange.to).isBefore(moment.utc(date)))
    },
    isDateRangeValid(params) {
        const dateRange = this.calcDateRange(params)
        if (!dateRange) return true
        return moment(dateRange.to).isAfter(dateRange.from)
    },
    stringifyDateRange(range, _params) {
        const params = _.defaults(_params, {
            format: 'YYYY-MM-DD HH:mm',
            dateFormat: 'YYYY-MM-DD',
            timeFormat: 'HH:mm',
            label: 'Zeitraum',
            from: 'von',
            until: 'bis'
        })
        const dateRange = this.calcDateRange(range)
        if (_.isEmpty(dateRange)) return ''

        const df = this.splitDate(dateRange.from, params)
        const dt = this.splitDate(dateRange.to, params)
        let ify = df ? `${params.label} ${params.from} ${df.datetime}` : ''
        if (df && dt) {
            if (df.date && df.date !== dt.date) {
                // TODO shorten end string if some month or some year
                //  depends on date format
                // let _end = end
                // if (start.substr(0, 8) === _end.substr(0, 8)) _end = _end.substr(8)
                // if (start.substr(0, 5) === _end.substr(0, 5)) _end = _end.substr(5)
                ify = `${params.label} ${df.datetime} ${params.until} ${dt.datetime}`
            }
            else if (!df.date) ify = `${params.label} ${params.until} ${dt.datetime}`
            else if (df.datetime !== dt.datetime) ify = `${params.label} ${df.datetime} ${params.until} ${dt.time}`
            else ify = `${params.label} ${df.datetime}`
        }

        return ify.trim()
    },
    // split date to formatted datetime, date and time
    splitDate(date, _params) {
        if (!date) return

        const params = _.defaults(_params, {
            format: 'YYYY-MM-DD HH:mm',
            dateFormat: 'YYYY-MM-DD',
            timeFormat: 'HH:mm'
        })
        const mom = moment(date)
        const _time = mom.format('HHmm')
        const _datetime = mom.format(params.format)
        const _date = mom.format(params.dateFormat)
        return {
            datetime: _time === '0000' || _time === '2359' ? _date : _datetime,
            date: _date,
            time: mom.format(params.timeFormat)
        }
    },

    fieldRandomizer(fields, exclude = []) {
        if (_.isEmpty(fields)) {
            return console.warn('fieldRandomizer: fields empty!')
        }

        exclude = exclude.concat(['spacer', 'divider'])
        const ft = []
        fields.filter(field => !field.disabled).forEach((field) => {
            if (!_.includes(exclude, field.type)) {
                const coldef = _.pick(field, ['name', 'type'])
                if (field.counter) coldef.size = field.counter
                if (coldef.type === 'combobox') {
                    coldef.items = field.items
                }
                else if (/^(slider|range)$/.test(coldef.type)) {
                    coldef.min = field.min
                    coldef.max = field.max
                }
                else if (coldef.type === 'sparkline') {
                    coldef.size = field.labels.length || field.value.length
                    coldef.min = field.min
                    coldef.max = field.max
                }
                ft.push(coldef)
            }
        })
        const fr = {}
        ft.forEach((cd) => {
            fr[cd.name] = { value: Randomizer.sample(cd) }
        })
        return fr
    },

    // create dialog to confirm block cloning
    // returns: undefined = canceled, false = not in group / at the end, true = to current group
    async cloneBlockConfirmation(block) {
        const answer = await Vue.prototype.$gridform.dialog({
            title: this.i18n('$vuetify.gfb.cloneblock.title'),
            subtitle: block.subtitle || block.label,
            text: block.hint,
            icon: block.icon,
            actions: [
                {
                    action: 'cancel',
                    text: this.i18n('$vuetify.actions.cancel'),
                    outlined: true
                },
                {
                    action: 'atEnd',
                    text: this.i18n('$vuetify.gfb.cloneblock.atEnd'),
                    outlined: true
                },
                {
                    action: 'inGroup',
                    text: this.i18n('$vuetify.gfb.cloneblock.inGroup'),
                    type: 'submit',
                    ref: 'submit',
                    accent: true
                }
            ]
        })
        return !answer || answer.action === 'cancel' ? void 0 : answer.action === 'inGroup'
    },

    /**
     * Convert counts to short format. 1000 = 1k, 100.000 = 100k, 1.000.000 = 1M
     * @param {(number|string)} count - number to format
     * @return {string} formatted count
     * @static
     * @public
     */
    formatCount(count) {
        count = parseInt(count, 10)
        if (!_.isNumber(count) || _.isNaN(count)) return String(count)

        let f = String(count)
        let s

        if (count > 1000000) {
            s = (f / 1000000).toFixed(1)
            s = s.replace(/\.0$/, '')
            f = '' + s + 'M'
        } else if (count > 100000) {
            s = (f / 1000).toFixed(0)
            f = '' + s + 'k'
        } else if (count > 1000) {
            s = (f / 1000).toFixed(1)
            s = s.replace(/\.0$/, '')
            f = '' + s + 'k'
        }

        return f
    },
    formatTimestamp(ms, pad = true) {
        ms = parseFloat(ms)
        const mn = Math.floor(ms / 60000)
        const s = Math.floor(ms / 1000 % 60).toFixed(0)
        const h = Math.floor(ms / 60000 / 60)
        let m = mn.toFixed()
        if (h) m = (mn % 60).toFixed()
        return (h ? h + ':' : '') + (m.length < 2 && pad ? '0' + m : m) + ':' + (s.length < 2 ? '0' + s : s)
    },

    bool(prop, propDefault) {
        if (/^true$/i.test(prop)) return true // catch true as string (url query parameter)
        if (/^false$/i.test(prop)) return false // catch false as string (url query parameter)
        return _.isBoolean(prop) ? prop : propDefault
    },
    toFixed(value) {
        return value.toFixed(1).replace(/.0$/, '')
    },

    //
    // localStorage
    //
    getStore(key, def) {
        if (!key) return

        let data = window.localStorage.getItem(key)
        if (!data) data = def

        return typeof data === 'string' ? this.parseJSON(data) : data
    },
    setStore(key, value) {
        if (!key) return

        let data = value
        if (typeof data === 'object') data = JSON.stringify(data)
        window.localStorage.setItem(key, data)
    },
    updateStore(key, value) {
        if (!key) return

        const current = this.getStore(key)
        this.setStore(key, _.extend(current, value))
    },
    removeStore(key) {
        if (!key) return

        window.localStorage.removeItem(key)
    },

    // debug and tests
    testDate(date) {
        console.log('')
        console.log('date', date)
        console.log('moment()', moment(date).format())
        console.log('moment().format', moment(date).format('YYYY-MM-DD HH:mm'))
        console.log('moment().local().format', moment(date).local().format('YYYY-MM-DD HH:mm'))
        console.log('moment.utc().local().format', moment.utc(date).local().format('YYYY-MM-DD HH:mm'))
        console.log('moment.utc()', moment.utc(date).format())
        console.log('')
    }
}

export default Helper
